/**
 * Pure indicator computation helpers for lightweight-charts.
 * All functions take an array of bars: { time, open, high, low, close, volume? }
 * and return arrays of `{ time, value }` (or `[lower, upper, …]` tuples) ready
 * to feed into a LineSeries / AreaSeries / HistogramSeries.
 *
 * Performance: every function is O(n) and allocates a single output array.
 * No deep cloning, no per-point object creation beyond the result row.
 * `useMemo` in the React layer caches results per `bars` reference.
 */

// ───── helpers ───────────────────────────────────────────────────────────
function ema(values, period) {
  const k = 2 / (period + 1);
  const out = new Array(values.length);
  let prev = null;
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (v == null || Number.isNaN(v)) { out[i] = prev; continue; }
    prev = prev == null ? v : v * k + prev * (1 - k);
    out[i] = prev;
  }
  return out;
}

function sma(values, period) {
  const out = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = 0; i < values.length; i += 1) {
    sum += values[i] || 0;
    if (i >= period) sum -= values[i - period] || 0;
    if (i >= period - 1) out[i] = sum / period;
  }
  return out;
}

function stdev(values, period, means) {
  const out = new Array(values.length).fill(null);
  for (let i = period - 1; i < values.length; i += 1) {
    let s = 0;
    const m = means[i];
    for (let j = i - period + 1; j <= i; j += 1) s += (values[j] - m) ** 2;
    out[i] = Math.sqrt(s / period);
  }
  return out;
}

// True Range
function trArr(bars) {
  const out = new Array(bars.length);
  out[0] = bars[0].high - bars[0].low;
  for (let i = 1; i < bars.length; i += 1) {
    const b = bars[i];
    const prevC = bars[i - 1].close;
    out[i] = Math.max(
      b.high - b.low,
      Math.abs(b.high - prevC),
      Math.abs(b.low - prevC),
    );
  }
  return out;
}

const toPoint = (bars, arr) =>
  bars.map((b, i) => ({ time: b.time, value: arr[i] })).filter((p) => p.value != null && Number.isFinite(p.value));

// ───── EMA (multi-period overlay) ────────────────────────────────────────
export function emaSeries(bars, period = 20) {
  const closes = bars.map((b) => b.close);
  return toPoint(bars, ema(closes, period));
}

// ───── Bollinger Bands (price overlay) ───────────────────────────────────
export function bollingerBands(bars, period = 20, mult = 2) {
  const closes = bars.map((b) => b.close);
  const mean = sma(closes, period);
  const sd = stdev(closes, period, mean);
  const upper = []; const middle = []; const lower = [];
  for (let i = 0; i < bars.length; i += 1) {
    if (mean[i] == null || sd[i] == null) continue;
    middle.push({ time: bars[i].time, value: mean[i] });
    upper.push({ time: bars[i].time, value: mean[i] + mult * sd[i] });
    lower.push({ time: bars[i].time, value: mean[i] - mult * sd[i] });
  }
  return { upper, middle, lower };
}

// ───── VWAP (cumulative; resets daily for intraday but here cumulative
//     across the visible window — close enough for daily charts) ─────────
export function vwap(bars) {
  let pv = 0; let v = 0;
  const out = [];
  for (let i = 0; i < bars.length; i += 1) {
    const b = bars[i];
    const typical = (b.high + b.low + b.close) / 3;
    const vol = b.volume || 0;
    pv += typical * vol;
    v += vol;
    out.push({ time: b.time, value: v > 0 ? pv / v : typical });
  }
  return out;
}

// ───── SuperTrend (10, 3) ─────────────────────────────────────────────────
export function supertrend(bars, period = 10, mult = 3) {
  const tr = trArr(bars);
  // Wilder's ATR
  const atr = new Array(bars.length).fill(null);
  let sum = 0;
  for (let i = 0; i < bars.length; i += 1) {
    if (i < period) { sum += tr[i]; if (i === period - 1) atr[i] = sum / period; continue; }
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  const up = []; const down = [];
  let trend = 1; // 1=up, -1=down
  let prevUp = 0; let prevDown = 0;
  for (let i = 0; i < bars.length; i += 1) {
    if (atr[i] == null) continue;
    const b = bars[i];
    const hl2 = (b.high + b.low) / 2;
    const basicUp = hl2 + mult * atr[i];
    const basicDown = hl2 - mult * atr[i];
    const finUp = i === 0 || prevUp === 0
      ? basicUp
      : (basicUp < prevUp || bars[i - 1].close > prevUp ? basicUp : prevUp);
    const finDown = i === 0 || prevDown === 0
      ? basicDown
      : (basicDown > prevDown || bars[i - 1].close < prevDown ? basicDown : prevDown);
    if (trend === 1 && b.close < finDown) trend = -1;
    else if (trend === -1 && b.close > finUp) trend = 1;
    if (trend === 1) up.push({ time: b.time, value: finDown });
    else down.push({ time: b.time, value: finUp });
    prevUp = finUp; prevDown = finDown;
  }
  return { up, down };
}

// ───── Donchian Channel (20) ─────────────────────────────────────────────
export function donchian(bars, period = 20) {
  const upper = []; const lower = []; const mid = [];
  for (let i = 0; i < bars.length; i += 1) {
    if (i < period - 1) continue;
    let hi = -Infinity; let lo = Infinity;
    for (let j = i - period + 1; j <= i; j += 1) {
      if (bars[j].high > hi) hi = bars[j].high;
      if (bars[j].low < lo) lo = bars[j].low;
    }
    upper.push({ time: bars[i].time, value: hi });
    lower.push({ time: bars[i].time, value: lo });
    mid.push({ time: bars[i].time, value: (hi + lo) / 2 });
  }
  return { upper, lower, mid };
}

// ───── Pivot Points (classic, from previous bar) ─────────────────────────
export function pivotPoints(bars) {
  // Returns flat lines from yesterday's H/L/C (relevant for intraday but
  // shown on any timeframe). We project the previous-bar pivot forward.
  if (bars.length < 2) return null;
  const p = bars[bars.length - 2];
  const pp = (p.high + p.low + p.close) / 3;
  const r1 = 2 * pp - p.low;
  const s1 = 2 * pp - p.high;
  const r2 = pp + (p.high - p.low);
  const s2 = pp - (p.high - p.low);
  return { pp, r1, r2, s1, s2 };
}

// ───── Fibonacci Retracement (auto from visible window) ──────────────────
export function fibRetrace(bars) {
  if (bars.length < 2) return null;
  let hi = -Infinity; let lo = Infinity;
  for (let i = 0; i < bars.length; i += 1) {
    if (bars[i].high > hi) hi = bars[i].high;
    if (bars[i].low < lo) lo = bars[i].low;
  }
  const range = hi - lo;
  return [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1].map((lvl) => ({
    lvl,
    price: hi - range * lvl,
  }));
}

// ───── RSI (14) ─────────────────────────────────────────────────────────
export function rsi(bars, period = 14) {
  const closes = bars.map((b) => b.close);
  const gains = new Array(bars.length).fill(0);
  const losses = new Array(bars.length).fill(0);
  for (let i = 1; i < bars.length; i += 1) {
    const d = closes[i] - closes[i - 1];
    gains[i] = d > 0 ? d : 0;
    losses[i] = d < 0 ? -d : 0;
  }
  const avgG = new Array(bars.length).fill(null);
  const avgL = new Array(bars.length).fill(null);
  let gSum = 0; let lSum = 0;
  for (let i = 1; i <= period; i += 1) { gSum += gains[i]; lSum += losses[i]; }
  avgG[period] = gSum / period;
  avgL[period] = lSum / period;
  for (let i = period + 1; i < bars.length; i += 1) {
    avgG[i] = (avgG[i - 1] * (period - 1) + gains[i]) / period;
    avgL[i] = (avgL[i - 1] * (period - 1) + losses[i]) / period;
  }
  const out = [];
  for (let i = 0; i < bars.length; i += 1) {
    if (avgG[i] == null) continue;
    const rs = avgL[i] === 0 ? 100 : avgG[i] / avgL[i];
    out.push({ time: bars[i].time, value: 100 - 100 / (1 + rs) });
  }
  return out;
}

// ───── MACD (12, 26, 9) ─────────────────────────────────────────────────
export function macd(bars, fast = 12, slow = 26, signal = 9) {
  const closes = bars.map((b) => b.close);
  const e1 = ema(closes, fast);
  const e2 = ema(closes, slow);
  const macdLine = e1.map((v, i) => (v != null && e2[i] != null ? v - e2[i] : null));
  const sigLine = ema(macdLine.map((v) => v ?? 0), signal);
  const hist = macdLine.map((v, i) => (v != null && sigLine[i] != null ? v - sigLine[i] : null));
  return {
    macd: bars.map((b, i) => ({ time: b.time, value: macdLine[i] })).filter((p) => p.value != null),
    signal: bars.map((b, i) => ({ time: b.time, value: sigLine[i] })).filter((p) => p.value != null),
    histogram: bars.map((b, i) => ({ time: b.time, value: hist[i], color: hist[i] >= 0 ? "rgba(16,185,129,0.65)" : "rgba(239,68,68,0.65)" })).filter((p) => p.value != null),
  };
}

// ───── ATR (14) ─────────────────────────────────────────────────────────
export function atr(bars, period = 14) {
  const tr = trArr(bars);
  const out = new Array(bars.length).fill(null);
  let sum = 0;
  for (let i = 0; i < bars.length; i += 1) {
    if (i < period) { sum += tr[i]; if (i === period - 1) out[i] = sum / period; continue; }
    out[i] = (out[i - 1] * (period - 1) + tr[i]) / period;
  }
  return toPoint(bars, out);
}

// ───── ADX (14) ─────────────────────────────────────────────────────────
export function adx(bars, period = 14) {
  const len = bars.length;
  if (len < period * 2) return [];
  const tr = trArr(bars);
  const plusDM = new Array(len).fill(0);
  const minusDM = new Array(len).fill(0);
  for (let i = 1; i < len; i += 1) {
    const upMove = bars[i].high - bars[i - 1].high;
    const downMove = bars[i - 1].low - bars[i].low;
    plusDM[i] = upMove > downMove && upMove > 0 ? upMove : 0;
    minusDM[i] = downMove > upMove && downMove > 0 ? downMove : 0;
  }
  // Smoothed sums (Wilder)
  const smTR = new Array(len).fill(null);
  const smP = new Array(len).fill(null);
  const smM = new Array(len).fill(null);
  let trS = 0; let pS = 0; let mS = 0;
  for (let i = 1; i <= period; i += 1) { trS += tr[i]; pS += plusDM[i]; mS += minusDM[i]; }
  smTR[period] = trS; smP[period] = pS; smM[period] = mS;
  for (let i = period + 1; i < len; i += 1) {
    smTR[i] = smTR[i - 1] - smTR[i - 1] / period + tr[i];
    smP[i] = smP[i - 1] - smP[i - 1] / period + plusDM[i];
    smM[i] = smM[i - 1] - smM[i - 1] / period + minusDM[i];
  }
  const dx = new Array(len).fill(null);
  for (let i = period; i < len; i += 1) {
    const pdi = (smP[i] / smTR[i]) * 100;
    const mdi = (smM[i] / smTR[i]) * 100;
    const sum = pdi + mdi || 1;
    dx[i] = (Math.abs(pdi - mdi) / sum) * 100;
  }
  const adxOut = new Array(len).fill(null);
  // First ADX = average of first `period` DX values
  let dxSum = 0; let count = 0;
  for (let i = period; i < period * 2 && i < len; i += 1) {
    if (dx[i] != null) { dxSum += dx[i]; count += 1; }
  }
  if (count) adxOut[period * 2 - 1] = dxSum / count;
  for (let i = period * 2; i < len; i += 1) {
    adxOut[i] = (adxOut[i - 1] * (period - 1) + dx[i]) / period;
  }
  return toPoint(bars, adxOut);
}

// ───── OBV (cumulative) ─────────────────────────────────────────────────
export function obv(bars) {
  const out = [];
  let cum = 0;
  for (let i = 1; i < bars.length; i += 1) {
    const v = bars[i].volume || 0;
    if (bars[i].close > bars[i - 1].close) cum += v;
    else if (bars[i].close < bars[i - 1].close) cum -= v;
    out.push({ time: bars[i].time, value: cum });
  }
  return out;
}
